/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { Order, OrderType } from '../util/services';
import { UiConstants } from '../../util/core-utils';
import { MemStorage } from '../util/mem-storage';
import { environment } from '../../../environments/environment';
import { DocumentGroupItem, DocumentLanguageItem } from '../../util/document/document-utils';
import { DocumentLinkList } from './document-link.service';
import { DisabledItem } from '../../util/search-utils';
import { RuntimeConfiguration } from '../runtime-configuration';
import { LoggedInUserStorage } from '../util/storages';
/* eslint-enable */

@Injectable()
export class DocumentLinkSearchService {

  constructor() {
  }

  getSearchData(request: DocumentLinkSearch.SearchDataGetRequest): Observable<DocumentLinkSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<DocumentLinkSearch.SearchDataResult>) => {
      try {
        observer.next(storage.read());
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  setSearchData(request: DocumentLinkSearch.SearchDataSetRequest): Observable<DocumentLinkSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<DocumentLinkSearch.SearchDataResult>) => {
      try {
        observer.next(storage.save(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  resetSearchData(): Observable<DocumentLinkSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<DocumentLinkSearch.SearchDataResult>) => {
      try {
        observer.next(storage.reset());
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  private createStorage(): DocumentLinkSearch.Storage {
    return new DocumentLinkSearch.Storage();
  }

}

export namespace DocumentLinkSearch {

  export interface SearchDataGetRequest {
  }

  export interface SearchDataSetRequest {
    searchData: SearchData;
  }

  export interface SearchDataResult {
    searchData: SearchData;
  }

  export interface SearchData {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<DocumentLinkList.OrderField>;
    name?: string;
    code?: string;
    value?: string;
    language_code?: DocumentLanguageItem;
    document_group?: DocumentGroupItem;
    disabled?: DisabledItem;
  }

  interface StoredSearchRawDataBase {
    version: number;
  }

  interface StoredSearchRawData extends StoredSearchRawDataBase {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<DocumentLinkList.OrderField>;
    name?: string;
    code?: string;
    value?: string;
    language_code?: DocumentLanguageItem;
    document_group?: DocumentGroupItem;
    disabled?: DisabledItem;
  }

  export class Storage {

    /**
     * Increment this version if you change the SearchData interface.
     */
    private static readonly VERSION: number = 2;

    private static readonly DEFAULT_SEARCH_DATA: SearchData = {
      itemsPerPage: UiConstants.pageNumbers[0],
      pageNumber: 1,
      order: {
        field: DocumentLinkList.OrderField.ID,
        type: OrderType.DESC
      },
      name: undefined,
      code: undefined,
      value: undefined,
      language_code: undefined,
      document_group: undefined,
      disabled: DisabledItem.active()
    };

    constructor() {
    }

    public read(): SearchDataResult {
      const key = this.generateKey();
      let rawBase: StoredSearchRawDataBase;
      try {
        const json = MemStorage.getItem(key);
        if (!json) {
          return this.createDefaultResult();
        }
        rawBase = JSON.parse(json);
      }
      catch (error) {
        return this.createDefaultResult();
      }
      if (Storage.VERSION === rawBase.version) {
        const raw: StoredSearchRawData = <StoredSearchRawData> rawBase;
        return this.fromRaw(raw);
      }
      return this.createDefaultResult();
    }

    public reset(): SearchDataResult {
      return this.save({
        searchData: Storage.DEFAULT_SEARCH_DATA,
      });
    }

    public save(request: SearchDataSetRequest): SearchDataResult {
      const key = this.generateKey();
      const raw = this.toRaw(request.searchData);
      const json = JSON.stringify(raw);
      MemStorage.setItem(key, json);
      return this.read();
    }

    private generateKey(): string {
      return 'UserId(' + LoggedInUserStorage.getInstance().getUserId() + ')-Search-Document-Link';
    }

    private createDefaultResult(): SearchDataResult {
      return {
        searchData: Storage.DEFAULT_SEARCH_DATA,
      };
    }

    private fromRaw(data: StoredSearchRawData): SearchDataResult {
      return {
        searchData: {
          itemsPerPage: data.itemsPerPage,
          pageNumber: data.pageNumber,
          order: data.order,
          name: data.name,
          code: data.code,
          value: data.value,
          language_code: data.language_code,
          document_group: data.document_group,
          disabled: data.disabled
        }
      };
    }

    private toRaw(data: SearchData): StoredSearchRawData {
      return {
        version: Storage.VERSION,
        itemsPerPage: data.itemsPerPage,
        pageNumber: data.pageNumber,
        order: data.order,
        name: data.name,
        code: data.code,
        value: data.value,
        language_code: data.language_code,
        document_group: data.document_group,
        disabled: data.disabled
      };
    }

  }

}
